import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { InlineVideoCall } from "components/InlineVideoCall/InlineVideoCall.jsx";

const VideoCallContext = createContext();

export const VideoCallProvider = ({ children, patientId, consultationId }) => {
  const [videoCredentials, setVideoCredentials] = useState(null);
  const [showInlineVideo, setShowInlineVideo] = useState(true);

  // Stable DOM node that hosts the live Chime call. The InlineVideoCall below
  // is portalled into it and stays mounted for the lifetime of this provider
  // (which lives in PatientLayout, above the route Outlet). We physically move
  // this node — into the consult-box slot when on the consult page, otherwise
  // into a hidden holder — so navigating between patient tabs reparents the
  // video instead of unmounting it. The Chime session and audio survive.
  const [videoHost] = useState(() => {
    const node = document.createElement("div");
    node.style.width = "100%";
    node.style.height = "100%";
    node.style.display = "flex";
    node.style.flexDirection = "column";
    return node;
  });
  const hiddenHolderRef = useRef(null);

  // Park the host back in the hidden holder (keeps audio playing off-screen).
  const parkHost = useCallback(() => {
    const holder = hiddenHolderRef.current;
    if (holder && videoHost.parentNode !== holder) {
      holder.appendChild(videoHost);
    }
  }, [videoHost]);

  const getSessionKey = () => {
    if (patientId && consultationId) {
      return `videoCall_patient_${patientId}_consultation_${consultationId}`;
    }
    if (patientId) {
      return `videoCall_patient_${patientId}`;
    }
    return null;
  };

  // If a call exists but no consult slot has adopted the host, keep it parked
  // in the hidden holder so it stays attached to the document (audio alive).
  useEffect(() => {
    if (videoCredentials && !videoHost.parentNode) {
      parkHost();
    }
  }, [videoCredentials, videoHost, parkHost]);

  useEffect(() => {
    const sessionKey = getSessionKey();
    if (sessionKey) {
      const savedState = sessionStorage.getItem(sessionKey);
      if (savedState) {
        try {
          const { credentials, showInline } = JSON.parse(savedState);
          setVideoCredentials(credentials);
          setShowInlineVideo(showInline);
        } catch (error) {
          console.error("Error restoring video call state:", error);
          sessionStorage.removeItem(sessionKey);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, consultationId]);

  useEffect(() => {
    const sessionKey = getSessionKey();
    if (sessionKey) {
      if (videoCredentials) {
        const stateToSave = {
          credentials: videoCredentials,
          showInline: showInlineVideo,
        };
        sessionStorage.setItem(sessionKey, JSON.stringify(stateToSave));
      } else {

        sessionStorage.removeItem(sessionKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoCredentials, showInlineVideo, patientId, consultationId]);

  const handleJoinVideo = (credentials) => {
    setVideoCredentials(credentials);
    setShowInlineVideo(true);
  };

  // Pop-out opens /video-call in a new tab so the user can park the
  // video in its own window. VideoCallPage reads `meetingId` from the
  // query string and `videoCallCredentials` from localStorage — we
  // stash credentials before opening so the new tab can pick them up.
  // After the new tab takes over the meeting we drop credentials in
  // this tab so the consult view falls back to its "Join video" state
  // — Chime only allows one connection per attendee token, so the
  // original tab needs to disconnect cleanly.
  const handlePopOutVideo = () => {
    if (!videoCredentials) return;
    const meetingId =
      videoCredentials.meetingId || videoCredentials.Meeting?.MeetingId;
    if (!meetingId) return;
    try {
      localStorage.setItem(
        "videoCallCredentials",
        JSON.stringify(videoCredentials)
      );
    } catch (error) {
      console.warn("Failed to persist video credentials for pop-out:", error);
    }
    window.open(
      `/video-call?meetingId=${encodeURIComponent(meetingId)}`,
      "_blank",
      "noopener,noreferrer"
    );
    setVideoCredentials(null);
    setShowInlineVideo(true);
  };

  const handleRestoreInlineVideo = () => {
    setShowInlineVideo(true);
  };

  const handleLeaveVideo = () => {
    setVideoCredentials(null);
    setShowInlineVideo(true);

  };

  const value = {
    videoCredentials,
    showInlineVideo,
    videoHost,
    parkHost,
    handleJoinVideo,
    handlePopOutVideo,
    handleRestoreInlineVideo,
    handleLeaveVideo,
  };

  const meetingId =
    videoCredentials?.meetingId || videoCredentials?.Meeting?.MeetingId;

  return (
    <VideoCallContext.Provider value={value}>
      {children}
      <div
        ref={hiddenHolderRef}
        aria-hidden
        style={{
          position: "absolute",
          width: 0,
          height: 0,
          overflow: "hidden",
          left: "-9999px",
          top: 0,
        }}
      />
      {!!videoCredentials &&
        createPortal(
          <InlineVideoCall
            credentials={videoCredentials}
            meetingId={meetingId}
            onPopOut={handlePopOutVideo}
            onLeave={handleLeaveVideo}
          />,
          videoHost
        )}
    </VideoCallContext.Provider>
  );
};

export const useVideoCall = () => {
  const context = useContext(VideoCallContext);
  if (!context) {

    return {
      videoCredentials: null,
      showInlineVideo: true,
      handleJoinVideo: () => console.warn("VideoCallProvider not found"),
      handlePopOutVideo: () => console.warn("VideoCallProvider not found"),
      handleRestoreInlineVideo: () => console.warn("VideoCallProvider not found"),
      handleLeaveVideo: () => console.warn("VideoCallProvider not found"),
    };
  }
  return context;
};